/**
 * Parse FAQ markdown string into Q&A pairs
 * Extracts questions from H3 headings (###) and answers from following text
 * @param {string} faqText - Markdown FAQ text
 * @returns {Array} Array of {question: string, answer: string} objects
 */
export const parseFaqFromMarkdown = (faqText) => {
  if (!faqText || typeof faqText !== 'string') return []
  
  // Convert escaped newlines to actual newlines
  let text = faqText.replace(/\\n/g, '\n').replace(/\\r/g, '\n')
  
  // Remove the main FAQ heading (## Häufig gestellte Fragen)
  text = text.replace(/^##\s+[^\n]+\n\n?/i, '')
  
  const lines = text.split('\n')
  const faqs = []
  let currentQuestion = null
  let currentAnswer = []
  
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i].trim()
    
    // Check if this is a question (H3 heading - ###)
    if (line.match(/^###\s+/)) {
      // Save previous Q&A if exists
      if (currentQuestion && currentAnswer.length > 0) {
        faqs.push({
          question: currentQuestion,
          answer: currentAnswer.join('\n').trim()
        })
      }
      // Start new question
      currentQuestion = line.replace(/^###\s+/, '').trim()
      currentAnswer = []
    } else if (currentQuestion) {
      // This is part of the answer
      if (line) {
        currentAnswer.push(line)
      } else if (currentAnswer.length > 0) {
        // Empty line in answer - preserve it
        currentAnswer.push('')
      }
    }
  }
  
  // Don't forget the last Q&A
  if (currentQuestion && currentAnswer.length > 0) {
    faqs.push({
      question: currentQuestion,
      answer: currentAnswer.join('\n').trim()
    })
  }
  
  return faqs
}

/**
 * Convert markdown-style text to HTML
 * Handles comprehensive markdown formatting: bold, italic, headings, lists, etc.
 * Works even when content has no line breaks (single-line JSON strings)
 * NOTE: Tables are not supported - use bullet lists instead
 */
export const markdownToHtml = (text) => {
  if (!text || typeof text !== 'string') return ''
  
  // Performance debugging
  const startTime = performance.now()
  
  let html = text
  
  // Handle escaped newlines (\n) - replace with actual newlines first, but be careful with list items
  // Convert JSON-escaped newlines to actual newlines before processing
  html = html.replace(/\\n/g, '\n')
  html = html.replace(/\\r/g, '\n')
  html = html.replace(/\\t/g, ' ')
  
  // Convert markdown links to HTML (before other processing)
  html = html.replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" target="_blank" rel="noopener noreferrer">$1</a>')
  
  // Remove any remaining table syntax that might cause parsing issues
  // Convert table rows to simple text (remove pipes and convert to plain text)
  // This handles any legacy table content that might still exist
  html = html.replace(/\|([^|\n]+(?:\s*\|\s*[^|\n]+)*)\|/g, (match, content) => {
    // Replace pipes with spaces and clean up
    return content.trim().replace(/\s*\|\s*/g, ' • ').trim()
  })
  
  // ========== HEADINGS ==========
  // Normalize: Add newlines before headings
  html = html.replace(/([.!?:])\s+(##+ [^#\n]+)/g, '$1\n\n$2')
  html = html.replace(/([^\n])(##+ )/g, (match, before, heading) => {
    if (!before.match(/\n$/)) {
      return before + '\n\n' + heading
    }
    return match
  })
  
  // ========== NUMBERED LISTS ==========
  // Add newlines before numbered lists
  html = html.replace(/([.!?:])\s+(\d+\.\s+)/g, '$1\n$2')
  html = html.replace(/([^\n])(\d+\.\s+)/g, (match, before, listItem) => {
    if (!before.match(/\d$/)) {
      return before + '\n' + listItem
    }
    return match
  })
  
  // ========== UNORDERED LISTS ==========
  // First, normalize list items that might have been split by newlines
  // If we have: * item\nmore text (without another *), join them
  html = html.replace(/([*\-+]\s+[^\n]+)\n([^\n*\-+\d#]+)/g, (match, itemStart, continuation) => {
    // If continuation doesn't start with list marker, heading, or number, it's part of the item
    if (!continuation.trim().match(/^([*\-+]|\d+\.|##?[#]?\s)/)) {
      return itemStart + ' ' + continuation.trim()
    }
    return match
  })
  
  // Add newlines before unordered lists
  html = html.replace(/([.!?:])\s+([*\-+]\s+)/g, '$1\n$2')
  html = html.replace(/([^\n*\-+])([*\-+]\s+)/g, (match, before, listItem) => {
    return before + '\n' + listItem
  })
  
  // Clean up multiple consecutive newlines
  html = html.replace(/\n{3,}/g, '\n\n')
  
  // ========== PROCESS LINE BY LINE ==========
  const lines = html.split('\n')
  const processedLines = []
  let inOrderedList = false
  let orderedListItems = []
  let inUnorderedList = false
  let unorderedListItems = []
  
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i]
    const trimmedLine = line.trim()
    
    // Skip empty lines but preserve structure
    if (!trimmedLine) {
      // Close any open structures
      if (inOrderedList) {
        processedLines.push(`<ol class="markdown-list">${orderedListItems.join('')}</ol>`)
        orderedListItems = []
        inOrderedList = false
      }
      if (inUnorderedList) {
        processedLines.push(`<ul class="markdown-list">${unorderedListItems.join('')}</ul>`)
        unorderedListItems = []
        inUnorderedList = false
      }
      processedLines.push('')
      continue
    }
    
    // Handle headings (H2, H3, H4) - also handle standalone # symbols
    // First remove standalone # or ## on their own lines (malformed headings)
    // Check in order from most specific to least specific
    if (trimmedLine.match(/^####+$/)) {
      // Skip standalone #### or more (malformed heading)
      continue
    }
    
    if (trimmedLine.match(/^###$/)) {
      // Skip standalone ### (malformed heading)
      continue
    }
    
    if (trimmedLine.match(/^##$/)) {
      // Skip standalone ## (malformed heading)
      continue
    }
    
    if (trimmedLine === '#') {
      // Skip standalone # (malformed heading)
      continue
    }
    
    // Now handle proper headings with content
    if (trimmedLine.match(/^###\s+/)) {
      if (inOrderedList) {
        processedLines.push(`<ol class="markdown-list">${orderedListItems.join('')}</ol>`)
        orderedListItems = []
        inOrderedList = false
      }
      if (inUnorderedList) {
        processedLines.push(`<ul class="markdown-list">${unorderedListItems.join('')}</ul>`)
        unorderedListItems = []
        inUnorderedList = false
      }
      const headingText = trimmedLine.replace(/^###\s+/, '').trim()
      let processedHeading = headingText
      processedHeading = processedHeading.replace(/\*\*([^*]+?)\*\*/g, '<strong>$1</strong>')
      processedLines.push(`<h4>${processedHeading}</h4>`)
      continue
    }
    
    if (trimmedLine.match(/^##\s+/)) {
      if (inOrderedList) {
        processedLines.push(`<ol class="markdown-list">${orderedListItems.join('')}</ol>`)
        orderedListItems = []
        inOrderedList = false
      }
      if (inUnorderedList) {
        processedLines.push(`<ul class="markdown-list">${unorderedListItems.join('')}</ul>`)
        unorderedListItems = []
        inUnorderedList = false
      }
      const headingText = trimmedLine.replace(/^##\s+/, '').trim()
      let processedHeading = headingText
      processedHeading = processedHeading.replace(/\*\*([^*]+?)\*\*/g, '<strong>$1</strong>')
      processedLines.push(`<h3>${processedHeading}</h3>`)
      continue
    }
    
    if (trimmedLine.match(/^#\s+/)) {
      if (inOrderedList) {
        processedLines.push(`<ol class="markdown-list">${orderedListItems.join('')}</ol>`)
        orderedListItems = []
        inOrderedList = false
      }
      if (inUnorderedList) {
        processedLines.push(`<ul class="markdown-list">${unorderedListItems.join('')}</ul>`)
        unorderedListItems = []
        inUnorderedList = false
      }
      const headingText = trimmedLine.replace(/^#\s+/, '').trim()
      let processedHeading = headingText
      processedHeading = processedHeading.replace(/\*\*([^*]+?)\*\*/g, '<strong>$1</strong>')
      processedLines.push(`<h2>${processedHeading}</h2>`)
      continue
    }
    
    // Skip any remaining table syntax (legacy content) - convert to plain text
    if (trimmedLine.startsWith('|') && trimmedLine.endsWith('|')) {
      // Convert table row to plain text with bullet points
      const cells = trimmedLine.split('|').map(c => c.trim()).filter(c => c && !c.match(/^[-:]+$/))
      if (cells.length > 0) {
        const asBullet = '* ' + cells.join(' • ')
        processedLines.push(asBullet)
        continue
      }
    }
    
    // Handle numbered lists (1. item)
    if (trimmedLine.match(/^\d+\. /)) {
      if (inUnorderedList) {
        processedLines.push(`<ul class="markdown-list">${unorderedListItems.join('')}</ul>`)
        unorderedListItems = []
        inUnorderedList = false
      }
      if (!inOrderedList) {
        inOrderedList = true
      }
      const itemText = trimmedLine.replace(/^\d+\. /, '').trim()
      // Process formatting in list items
      let processedItem = itemText
      processedItem = processedItem.replace(/\*\*([^*]+?)\*\*/g, '<strong>$1</strong>')
      processedItem = processedItem.replace(/\*([^*\n]+?)\*/g, '<em>$1</em>')
      processedItem = processedItem.replace(/_([^_\n]+?)_/g, '<em>$1</em>')
      orderedListItems.push(`<li>${processedItem}</li>`)
      continue
    } else if (inOrderedList) {
      // Check if this is a continuation of the previous list item
      if (line.match(/^\s{2,}/) && !trimmedLine.match(/^(##|###|#|\d+\.|[*\-+])/)) {
        // This is a continuation line - append to the last list item
        if (orderedListItems.length > 0) {
          const lastItem = orderedListItems[orderedListItems.length - 1]
          // Remove closing </li> tag, add content, then re-add </li>
          const updatedItem = lastItem.replace(/<\/li>$/, '')
          let continuationText = trimmedLine
          continuationText = continuationText.replace(/\*\*([^*]+?)\*\*/g, '<strong>$1</strong>')
          continuationText = continuationText.replace(/\*([^*\n]+?)\*/g, '<em>$1</em>')
          continuationText = continuationText.replace(/_([^_\n]+?)_/g, '<em>$1</em>')
          orderedListItems[orderedListItems.length - 1] = `${updatedItem} ${continuationText}</li>`
        }
        continue
      }
      // End of ordered list
      if (orderedListItems.length > 0) {
        processedLines.push(`<ol class="markdown-list">${orderedListItems.join('')}</ol>`)
      }
      orderedListItems = []
      inOrderedList = false
    }
    
    // Handle unordered lists (* item, - item, + item)
    if (trimmedLine.match(/^[*\-+] /)) {
      if (!inUnorderedList) {
        inUnorderedList = true
      }
      const itemText = trimmedLine.replace(/^[*\-+] /, '').trim()
      // Process formatting in list items
      let processedItem = itemText
      processedItem = processedItem.replace(/\*\*([^*]+?)\*\*/g, '<strong>$1</strong>')
      processedItem = processedItem.replace(/\*([^*\n]+?)\*/g, '<em>$1</em>')
      processedItem = processedItem.replace(/_([^_\n]+?)_/g, '<em>$1</em>')
      unorderedListItems.push(`<li>${processedItem}</li>`)
      continue
    } else if (inUnorderedList) {
      // Check if this is a continuation of the previous list item
      // (e.g., wrapped text or content that's part of the same item)
      // If line starts with 2+ spaces, it's likely a continuation
      if (line.match(/^\s{2,}/) && !trimmedLine.match(/^(##|###|#|\d+\.|[*\-+])/)) {
        // This is a continuation line - append to the last list item
        if (unorderedListItems.length > 0) {
          const lastItem = unorderedListItems[unorderedListItems.length - 1]
          // Remove closing </li> tag, add content, then re-add </li>
          const updatedItem = lastItem.replace(/<\/li>$/, '')
          let continuationText = trimmedLine
          continuationText = continuationText.replace(/\*\*([^*]+?)\*\*/g, '<strong>$1</strong>')
          continuationText = continuationText.replace(/\*([^*\n]+?)\*/g, '<em>$1</em>')
          continuationText = continuationText.replace(/_([^_\n]+?)_/g, '<em>$1</em>')
          unorderedListItems[unorderedListItems.length - 1] = `${updatedItem} ${continuationText}</li>`
        }
        continue
      }
      // End of unordered list
      if (unorderedListItems.length > 0) {
        processedLines.push(`<ul class="markdown-list">${unorderedListItems.join('')}</ul>`)
      }
      unorderedListItems = []
      inUnorderedList = false
    }
    
    // Regular paragraph text
    processedLines.push(line)
  }
  
  // Close any remaining open structures
  if (inOrderedList && orderedListItems.length > 0) {
    processedLines.push(`<ol class="markdown-list">${orderedListItems.join('')}</ol>`)
  }
  if (inUnorderedList && unorderedListItems.length > 0) {
    processedLines.push(`<ul class="markdown-list">${unorderedListItems.join('')}</ul>`)
  }
  
  // Join lines and split by double newlines for paragraphs
  html = processedLines.join('\n')
  
  // Split into paragraphs (double newlines)
  const paragraphs = html.split(/\n\n+/)
  
  // Process each paragraph
  html = paragraphs.map(para => {
    para = para.trim()
    if (!para) return ''
    
    // Skip if already wrapped in HTML tags (headings, lists)
    if (para.match(/^<(h[1-6]|ul|ol|li)/)) {
      return para
    }
    
    // Process formatting in regular paragraphs
    para = para.replace(/\*\*([^*]+?)\*\*/g, '<strong>$1</strong>')
    para = para.replace(/\*([^*]+?)\*/g, '<em>$1</em>')
    para = para.replace(/_([^_]+?)_/g, '<em>$1</em>')
    
    // Convert single newlines to <br> within paragraphs
    para = para.replace(/\n(?!\n)/g, '<br>')
    
    // Wrap in paragraph
    return `<p>${para}</p>`
  }).join('')
  
  // Performance debugging
  const endTime = performance.now()
  const duration = endTime - startTime
  if (duration > 10) { // Only log if it takes more than 10ms
    console.warn(`⚠️ markdownToHtml took ${duration.toFixed(2)}ms for ${text.length} chars`)
  }
  
  return html
}
